import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/dialog";
import PageHeader from "./-components/PageHeader";
import Pagination from "components/Pagination";
import LoadingPop from "components/popup/LoadingPop";
import ConsoleTable, { DataListProps } from "./-components/ConsoleTable";
import { useGetConsoleBlImages } from "hooks/console/useGetConsoleBlImages";
import ConsoleHrozontalHeadTable from "./-components/ConsolehorizontalHeadTable";
import { allowedMimeTypes, makeIntComma, toastErrorSyle } from "hooks/utils/util";
import { usePostConsoleBlImages } from "hooks/console/usePostConsoleBlImages";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import { useLocation, useSearchParams } from "react-router-dom";
import printJS from "print-js";
import { downloadBLImage } from "hooks/air/useGetBLImageDown";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { useDeleteConsoleBlImages } from "hooks/console/useDeleteConsoleBlImages";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";

const theadStyle = "text-[#222222] text-[16px] text-center";

const initialSearchForm = {
    page: 1,
    limit: "10",
    s_bl_no: "",
};

export default function ConsoleAirBlImage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState(initialSearchForm);
    const [searchValue, setSearchValue] = useState("");
    const [selectedItem, setSelectedItem] = useState<DataListProps | null>(null);
    const [fileState, setFileState] = useState<File[]>([]);
    const viewDetailDialogRef = useRef<HTMLButtonElement | null>(null);
    const viewImageDialogRef = useRef<HTMLButtonElement | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const location = useLocation();
    const { data, refetch, isLoading } = useGetConsoleBlImages(searchForm);
    const { mutate, isPending } = usePostConsoleBlImages();
    const { mutate: deleateImage } = useDeleteConsoleBlImages();

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const handleUploadImage = () => {
        // console.log("이미지 업로드:", row);
        if (selectedItem) {
            mutate(
                {
                    s_bl_no: selectedItem.s_bl_no,
                    b_file: fileState,
                },
                {
                    onSuccess: (data: any) => {
                        toast.success("성공적으로 업로드 되었습니다.");
                        refetch();
                    },
                    onError: (error: any) => {
                        const message = error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
        }
        viewDetailDialogRef.current?.click();
    };

    const handleShowUploadDialog = (row: DataListProps) => {
        setSelectedItem(row);
        viewDetailDialogRef.current?.click();
    };

    const handleShowImageDialog = (row: DataListProps) => {
        setSelectedItem(row);
        viewImageDialogRef.current?.click();
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        handleSearch();
    };

    const handleSearch = () => {
        setSearchForm(prev => ({ ...prev, s_bl_no: searchValue, page: 1 }));
    };

    const handleDeleteBlImage = (bl_no: string) => {
        deleateImage(
            {
                s_bl_no: bl_no,
            },
            {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };
    const inputTooltipDescription = fileState?.length > 0 ? `선택된 파일 ${fileState.length}개, ${fileState[0].name}` : "선택된 파일 없음";

    return (
        <>
            <Seo type="console" pageTitle="B/L 이미지" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="B/L 이미지" />

                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form onSubmit={onSubmit}>
                                <div className="flex justify-end items-center gap-2">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="10">10개</SelectItem>
                                            <SelectItem value="20">20개</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            placeholder="B/L No를 입력해주세요."
                                            value={searchValue}
                                            onChange={e => setSearchValue(e.target.value)}
                                            className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                        />
                                        <ReadingGlasses onClick={handleSearch} />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px] border-t border-[#dddddd]">
                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "10%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                return (
                                                    <label
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {/* <input
                                                        type="checkbox"
                                                        checked={selectedRows.includes(row.s_bl_no)}
                                                        onChange={() => toggleRow(row.list_no)}
                                                    /> */}
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },

                                        {
                                            title: "B/L No",
                                            width: "30%",
                                            key: "s_bl_no",
                                            className: theadStyle,
                                        },
                                        {
                                            title: "파일명",
                                            width: "30%",
                                            key: "b_file",
                                            className: twMerge(theadStyle, "text-[14px] text-[#666666]"),
                                            content: (row: DataListProps) => {
                                                if (row.b_file) {
                                                    return (
                                                        <div className="flex items-center gap-2 justify-center">
                                                            <div
                                                                onClick={() => handleShowImageDialog(row)}
                                                                className="hover:underline cursor-pointer truncate"
                                                            >
                                                                {row.b_file}
                                                            </div>

                                                            <ConfirmDialog
                                                                title="B/L 이미지 삭제"
                                                                trigger={open => <Close className="cursor-pointer" onClick={open} />}
                                                                handleConfirm={() => handleDeleteBlImage(row.s_bl_no)}
                                                            />
                                                        </div>
                                                    );
                                                } else {
                                                    return <p>-</p>;
                                                }
                                            },
                                        },
                                        {
                                            title: "등록일",
                                            width: "20%",
                                            key: "s_date",
                                            className: twMerge(theadStyle, "text-[14px] text-[#999999]"),
                                        },
                                        {
                                            title: "비고",
                                            width: "20%",
                                            key: "bigo",
                                            content: (row: DataListProps) => (
                                                <button
                                                    onClick={() => handleShowUploadDialog(row)}
                                                    className="bg-[#192030] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                                >
                                                    이미지 업로드
                                                </button>
                                            ),
                                            className: theadStyle,
                                        },
                                    ]}
                                    tbody={data?.data_list || []}
                                    isLoading={isLoading}
                                    uniqueKeyName="s_bl_no"
                                    className="mb-[40px] border-t border-[#dddddd]"
                                />
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    // setSearchForm(prev => ({ ...prev, page: newPage }));
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                onOpenChange={open => {
                    if (!open) {
                        setSelectedItem(null);
                    }
                }}
            >
                <DialogTrigger ref={viewImageDialogRef} className="absolute" />
                <DialogContent className="bg-[#F7F6FB] p-0 gap-0 w-full max-w-[1000px]">
                    <DialogHeader className="border-b border-[#dddddd] p-[32px_40px_20px]">
                        <DialogTitle className="text-[24px]">B/L 이미지</DialogTitle>
                        <DialogDescription hidden />
                    </DialogHeader>
                    <div className="py-[20px] flex flex-col gap-5 items-center">
                        <div className="px-[40px] max-h-[70dvh] overflow-auto w-full">
                            <div className="bg-white p-5 w-full">
                                <div className="mb-[40px]">
                                    {isPending && <LoadingPop />}

                                    {selectedItem && (
                                        <div id="print-area">
                                            <img
                                                src={"http://bl.ilshingroup.com/blimg/" + selectedItem.b_file}
                                                alt=""
                                                width={850}
                                                height="auto"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                                onClick={() => viewImageDialogRef.current?.click()}
                            >
                                닫기
                            </button>
                            <button
                                className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                                onClick={() => {
                                    if (selectedItem?.b_file) {
                                        const blImageUrl = "http://bl.ilshingroup.com/blimg/" + selectedItem.b_file;
                                        printJS({
                                            printable: blImageUrl,
                                            type: "image",
                                            style: `
                                                    @page { margin: 0; size: auto; }
                                                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                                                    img { max-width: 100%; height: auto; }
                                                `,
                                        });
                                    }
                                }}
                            >
                                인쇄하기
                            </button>
                            <button
                                className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                                onClick={() => {
                                    if (selectedItem?.s_bl_no) downloadBLImage(selectedItem.s_bl_no, `${selectedItem.s_bl_no}.jpg`);
                                }}
                            >
                                다운로드
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                onOpenChange={open => {
                    if (!open) {
                        setSelectedItem(null);
                        setFileState([]);
                    }
                }}
            >
                <DialogTrigger ref={viewDetailDialogRef} className="absolute" />
                <DialogContent className="bg-[#F7F6FB] p-0 gap-0 w-full max-w-[800px]">
                    <DialogHeader className="border-b border-[#dddddd] p-[32px_40px_20px]">
                        <DialogTitle className="text-[24px]">B/L 이미지 수정</DialogTitle>
                        <DialogDescription hidden />
                    </DialogHeader>
                    <div className="py-[20px] flex flex-col gap-5 items-center">
                        <div className="px-[40px] max-h-[60dvh] overflow-auto w-full">
                            <div className="bg-white p-5 w-full">
                                <div className="mb-[40px]">
                                    {isPending && <LoadingPop />}
                                    {selectedItem && (
                                        <ConsoleHrozontalHeadTable
                                            thead={[
                                                {
                                                    title: "B/L NO",
                                                    key: "s_bl_no",
                                                    thClassName: "py-4 px-3",
                                                    tdClassName: "py-4 px-3",
                                                },
                                                {
                                                    title: "이미지",
                                                    key: "b_file",
                                                    content: (row: any, tbodyData: DataListProps) => {
                                                        return (
                                                            <div>
                                                                <div className="flex items-center gap-x-1 px-2">
                                                                    <button
                                                                        onClick={() => fileRef.current?.click()}
                                                                        className="border rounded-[3px] px-2 py-1 bg-[#dddddd] text-black"
                                                                    >
                                                                        파일선택
                                                                    </button>
                                                                    <input
                                                                        type="file"
                                                                        ref={fileRef}
                                                                        title={inputTooltipDescription}
                                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                                            const { files } = event.target;

                                                                            if (files && files.length > 0) {
                                                                                // 유효한 파일인지 확인
                                                                                const invalidFiles = Array.from(files).filter(
                                                                                    file => !allowedMimeTypes.includes(file.type),
                                                                                );
                                                                                if (invalidFiles.length > 0) {
                                                                                    alert("등록 할 수 없는 파일 형식 입니다.");
                                                                                    return;
                                                                                }

                                                                                setFileState(Array(files[0]));
                                                                            }
                                                                            event.target.value = ""; //input value 초기화 accept=""
                                                                        }}
                                                                        className="absolute appearance-none opacity-0"
                                                                    />
                                                                    <div className="flex-1">
                                                                        {fileState.length > 0 ? fileState[0].name : "선택된 파일 없음"}
                                                                    </div>
                                                                </div>
                                                                {row && <p className="p-2">{row}</p>}
                                                            </div>
                                                        );
                                                    },
                                                    thClassName: "py-4 px-3",
                                                    tdClassName: "py-4 px-3",
                                                },
                                            ]}
                                            tbody={selectedItem}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                                onClick={handleUploadImage}
                            >
                                수정
                            </button>
                            <button
                                className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                                onClick={() => viewDetailDialogRef.current?.click()}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
